/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FieldArray, FormikProps } from 'formik';
import { memo, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  OutlinedInput,
  Tooltip,
  Typography,
} from '@mui/material';

import { HUSpin } from '../../../../../../components/HUSpin/HUSpin';
import { Namespaces } from '../../../../../../i18n';
import { useFormStyles } from '../../../../../../styles/form';
import { useValidationTranslation } from '../../../../../../utils/useValidationTranslation';
import { useFirewallSettingsStyles } from './FirewallSettings.styles';
import { FirewallSettingsFormValues } from './FirewallSettings.types';

type FirewallSettingsFormProps = FormikProps<FirewallSettingsFormValues> & {
  isProcessing: boolean;
};

export const FirewallSettingsForm = memo<FirewallSettingsFormProps>(props => {
  const { values, handleSubmit, errors, handleChange, handleBlur, touched, resetForm, dirty, isValid, isProcessing } =
    props;

  const { t } = useTranslation(Namespaces.Cluster);
  const { vtr } = useValidationTranslation();

  const classes = useFirewallSettingsStyles();
  const formClasses = useFormStyles();

  const inputHasError = (index: number) => {
    if (Array.isArray(errors.allowedSourceRanges)) {
      return !!touched.allowedSourceRanges && !!errors.allowedSourceRanges[index];
    }
    return false;
  };

  const inputGetError = (index: number) => {
    if (Array.isArray(errors.allowedSourceRanges)) {
      return errors.allowedSourceRanges[index] || '';
    }
    return '';
  };

  const isReachedMaxLimit = useMemo(() => values.allowedSourceRanges.length === 10, [values.allowedSourceRanges]);

  const reachedMaxTitle = useMemo(
    () => (isReachedMaxLimit ? t('details.acl.firewall.max_limit') : ''),
    [isReachedMaxLimit, t],
  );

  return (
    <HUSpin spinning={isProcessing}>
      <form onSubmit={handleSubmit}>
        <FieldArray
          name="allowedSourceRanges"
          render={helpers => (
            <div>
              <Typography color={typeof errors['allowedSourceRanges'] === 'string' ? 'error' : 'text'}>
                {t('details.acl.firewall.form.allowed_source_ranges')}:
              </Typography>
              {typeof errors['allowedSourceRanges'] === 'string' && (
                <Typography className={cx(classes.error, classes.hint, formClasses.widthMedium)}>
                  {vtr(errors['allowedSourceRanges'], {
                    field: t('details.acl.firewall.form.allowed_source_ranges'),
                    maxLength: 10,
                  })}
                </Typography>
              )}
              <Typography className={cx(classes.hint, formClasses.widthMedium)}>
                <Trans i18nKey={`${Namespaces.Cluster}:details.acl.firewall.form.hint`} components={{ b: <b /> }} />
              </Typography>
              <div className={classes.fields}>
                {values.allowedSourceRanges.map((val, idx) => (
                  <div key={idx} className={classes.field}>
                    <FormControl
                      variant="outlined"
                      className={cx(formClasses.formControl, formClasses.widthDefault, classes.helperText)}
                      error={inputHasError(idx)}
                    >
                      <OutlinedInput
                        name={`allowedSourceRanges.${idx}`}
                        placeholder="192.168.0.1/32"
                        fullWidth
                        value={val}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        data-cy={`cluster-page-firewall-settings-ip-address-${idx}`}
                      />
                      {inputHasError(idx) && (
                        <FormHelperText>
                          {vtr(inputGetError(idx), {
                            field: t('details.acl.firewall.form.ip_address'),
                          })}
                        </FormHelperText>
                      )}
                    </FormControl>
                    <Tooltip title={t('details.acl.firewall.form.actions.remove')}>
                      <IconButton
                        onClick={() => helpers.remove(idx)}
                        data-cy={`cluster-page-firewall-settings-remove-${idx}`}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Tooltip>
                  </div>
                ))}
                <Tooltip title={reachedMaxTitle} sx={{ textAlign: 'center' }}>
                  <span>
                    <Button
                      onClick={() => helpers.push('')}
                      sx={{ mt: '5px' }}
                      startIcon={<AddIcon />}
                      data-cy="cluster-page-firewall-settings-add"
                      disabled={isReachedMaxLimit}
                    >
                      {t('details.acl.firewall.form.actions.add')}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </div>
          )}
        />
        <Grid container sx={{ alignItems: 'center', gap: '10px', mt: '30px' }}>
          <Grid item>
            <Button variant="outlined" onClick={() => resetForm()} disabled={!dirty}>
              {t(`${Namespaces.Common}:form.cancel`)}
            </Button>
          </Grid>
          <Grid item>
            <Button variant="contained" type="submit" disabled={!dirty || !isValid}>
              {t(`${Namespaces.Common}:modal.submit`)}
            </Button>
          </Grid>
        </Grid>
      </form>
    </HUSpin>
  );
});
